const PROFILE_AND_COMPANY_VARIABLES = [
    "$prénom_profil",
    "$nom_profil",
    "$raison_sociale",
    "$nom_commercial",
    "$téléphone",
    "$email",
];
const APPOINTMENT_VARIABLES = [
    "$civilité_client",
    "$prénom_client",
    "$nom_client",
    "$heure_rdv",
    "$date_rdv",
    "$lieu_rdv",
    ...PROFILE_AND_COMPANY_VARIABLES,
];
const UPDATE_EXTRA_VARIABLES = ["$ancienne_date_rdv", "$ancienne_heure_rdv"];
const INVOICE_VARIABLES = [
    "$civilité_client",
    "$prénom_client",
    "$nom_client",
    "$période_prestation",
    "$numéro_facture",
    ...PROFILE_AND_COMPANY_VARIABLES,
];
const SCHEDULED_REMINDER_EXTRA_VARIABLES = ["$lien_prise_rdv"];
/**
 * No `$prénom_profil`/`$nom_profil` (no specific staff member context) and no
 * `$date_rdv`/`$heure_rdv`/`$lieu_rdv` (sent when the customer has no future
 * appointment) — only what's always resolvable without a `sourceEventId` (#1301).
 */
const SCHEDULED_REMINDER_VARIABLES = [
    "$civilité_client",
    "$prénom_client",
    "$nom_client",
    "$raison_sociale",
    "$nom_commercial",
    "$téléphone",
    "$email",
    ...SCHEDULED_REMINDER_EXTRA_VARIABLES,
];
/**
 * Signature is appended after every email body (appointments + invoices).
 * Only profile / company variables are exposed since the signature is a
 * global setting that has no notion of a specific customer or appointment.
 */
const SIGNATURE_VARIABLES = PROFILE_AND_COMPANY_VARIABLES;
/**
 * Variables allowed per template kind. The `update` kind exposes two extra
 * variables to reference the previous appointment date/time. Invoice kinds
 * share their own variable list (period + invoice number, no appointment
 * date/time). The `signature` kind only exposes profile / company variables.
 */
export const EMAIL_TEMPLATE_VARIABLES_BY_KIND = {
    reminder: APPOINTMENT_VARIABLES,
    creation: APPOINTMENT_VARIABLES,
    update: [...APPOINTMENT_VARIABLES, ...UPDATE_EXTRA_VARIABLES],
    invoice: INVOICE_VARIABLES,
    invoice_reminder: INVOICE_VARIABLES,
    credit_note: INVOICE_VARIABLES,
    scheduled_reminder: SCHEDULED_REMINDER_VARIABLES,
    signature: SIGNATURE_VARIABLES,
};
const VARIABLE_TOKEN_REGEX = /\$[\p{L}_][\p{L}\p{N}_]*/gu;
/**
 * Render a French civility prefix from the customer gender column. When the
 * gender is unknown we fall back to "M./Mme" so the salutation still reads
 * naturally without exposing the customer's missing data to them.
 */
export function resolveCivility(gender) {
    switch (gender) {
        case "mr":
            return "M.";
        case "mrs":
            return "Mme";
        default:
            return "M./Mme";
    }
}
/**
 * Resolve `$prénom_client`/`$nom_client` with a repli on `companyLegalName`
 * when the customer is a Pro account with no named contact (#1301) — same
 * pattern as `resolveCivility()`, which replies on "M./Mme".
 */
export function resolveCustomerName(customer) {
    if (customer.firstName || customer.familyName) {
        return { firstName: customer.firstName ?? "", lastName: customer.familyName ?? "" };
    }
    const legalName = customer.companyLegalName ?? "";
    return { firstName: legalName, lastName: legalName };
}
/**
 * Substitute every known `$variable_fr` token with its resolved value.
 * Unknown tokens (e.g. `$truc` not in the whitelist) are left untouched
 * so a typo never strips text from the rendered email.
 */
export function renderEmailTemplate(template, vars) {
    return template.replace(VARIABLE_TOKEN_REGEX, (match) => {
        if (Object.prototype.hasOwnProperty.call(vars, match)) {
            const value = vars[match];
            return value ?? match;
        }
        return match;
    });
}
export function renderLienPriseRdv(origin, slug) {
    return `Vous pouvez prendre rendez-vous directement en ligne :
${origin}/pro/${slug}/reserver`;
}
/**
 * Return the list of `$xxx` tokens present in the template that are NOT in
 * the whitelist for the given kind. Used by the UI to warn the user about
 * typos at save time. Result is deduplicated and ordered by first appearance.
 */
export function findUnknownVariables(template, kind) {
    const matches = template.match(VARIABLE_TOKEN_REGEX) ?? [];
    const known = new Set(EMAIL_TEMPLATE_VARIABLES_BY_KIND[kind]);
    const seen = new Set();
    const result = [];
    for (const match of matches) {
        if (known.has(match) || seen.has(match)) {
            continue;
        }
        seen.add(match);
        result.push(match);
    }
    return result;
}
export const DEFAULT_REMINDER_EMAIL_TEMPLATE = `Bonjour $civilité_client $nom_client,

Je me permets de vous rappeler notre rendez-vous prévu demain à $heure_rdv.

À demain.`;
export const DEFAULT_CREATION_EMAIL_TEMPLATE = `Bonjour $civilité_client $nom_client,

Votre rendez-vous est confirmé pour le $date_rdv à $heure_rdv.

À bientôt.`;
export const DEFAULT_UPDATE_EMAIL_TEMPLATE = `Bonjour $civilité_client $nom_client,

Votre rendez-vous initialement prévu le $ancienne_date_rdv à $ancienne_heure_rdv a été déplacé.

Nouvelle date : le $date_rdv à $heure_rdv.

À bientôt.`;
export const DEFAULT_INVOICE_EMAIL_TEMPLATE = `Bonjour $civilité_client $nom_client,

Veuillez trouver ci-joint la facture n°$numéro_facture relative à la prestation $période_prestation.

Je reste à votre disposition pour toute question.`;
export const DEFAULT_INVOICE_REMINDER_EMAIL_TEMPLATE = `Bonjour $civilité_client $nom_client,

Sauf erreur de ma part, le règlement de la facture n°$numéro_facture relative à la prestation $période_prestation ne m'est pas encore parvenu.

Je vous remercie de bien vouloir y donner suite dès que possible.`;
export const DEFAULT_CREDIT_NOTE_EMAIL_TEMPLATE = `Bonjour $civilité_client $nom_client,

Veuillez trouver ci-joint la facture d'avoir n°$numéro_facture relative à la prestation $période_prestation.

Je reste à votre disposition pour toute question.`;
export const DEFAULT_SCHEDULED_REMINDER_EMAIL_TEMPLATE = `Bonjour $prénom_client,

Le moment est peut-être venu de reprendre rendez-vous.
N'hésitez pas à me contacter pour en planifier un.

$lien_prise_rdv`;
/**
 * Default signature appended after every email body. Contains the polite
 * closing, the sender block, and the no-reply disclaimer (mytour sends from
 * an unmonitored address — recipients must reply to the pro's own email).
 */
export const DEFAULT_EMAIL_SIGNATURE = `Cordialement,

$prénom_profil - $nom_commercial
$téléphone
$email

---
Message envoyé automatiquement depuis MyTour pour le compte de $nom_commercial.
Pour répondre, écrivez à $email.`;
export function getDefaultEmailTemplate(kind) {
    switch (kind) {
        case "reminder":
            return DEFAULT_REMINDER_EMAIL_TEMPLATE;
        case "creation":
            return DEFAULT_CREATION_EMAIL_TEMPLATE;
        case "update":
            return DEFAULT_UPDATE_EMAIL_TEMPLATE;
        case "invoice":
            return DEFAULT_INVOICE_EMAIL_TEMPLATE;
        case "invoice_reminder":
            return DEFAULT_INVOICE_REMINDER_EMAIL_TEMPLATE;
        case "credit_note":
            return DEFAULT_CREDIT_NOTE_EMAIL_TEMPLATE;
        case "scheduled_reminder":
            return DEFAULT_SCHEDULED_REMINDER_EMAIL_TEMPLATE;
        case "signature":
            return DEFAULT_EMAIL_SIGNATURE;
    }
}
//# sourceMappingURL=email-template.js.map